import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { db } from "../firebaseConfig";
import { collection, addDoc, Timestamp, updateDoc, doc, arrayUnion } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const InvestmentModal = ({
  isOpen,
  onClose,
  carName,
  carID,
  moneyRaised,
  totalValue,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [ownershipPercentage, setOwnershipPercentage] = useState(0);
  const [clientSecret, setClientSecret] = useState("");

  const stripe = useStripe();
  const elements = useElements();

  const maxInvestment = totalValue - moneyRaised;
  const maxOwnershipPercentage = Math.floor((maxInvestment / totalValue) * 100);

  // Rental Discount Logic (1% discount per 6% ownership)
  const calculateRentalDiscount = (percentage) => {
    return Math.floor(percentage * 0.6);
  };

  // Free Days Logic (1 free day for every 3.3% ownership)
  const calculateFreeDays = (percentage) => {
    return Math.floor(percentage / 3.3);
  };

  const handleSliderChange = (value) => {
    const calculatedAmount = Math.round((value / 100) * totalValue);
    setOwnershipPercentage(value);
    setInvestmentAmount(
      calculatedAmount > maxInvestment ? maxInvestment : calculatedAmount
    );
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/,/g, "").replace(/\D/g, "");
    if (value === "") {
      setInvestmentAmount("");
      setOwnershipPercentage(0);
    } else {
      const numericValue = Math.min(parseInt(value, 10), maxInvestment);
      setInvestmentAmount(numericValue);
      setOwnershipPercentage(Math.round((numericValue / totalValue) * 100));
    }
  };

  const createPaymentIntent = async () => {
    if (!investmentAmount) {
      alert("Please enter an investment amount first.");
      return;
    }
    try {
      const amountCents = parseInt(investmentAmount, 10) * 100;
      const res = await fetch(
        "https://us-central1-gearshare-41e19.cloudfunctions.net/api/create-payment-intent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ amount: amountCents }),
        }
      );
      const data = await res.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        alert("Error creating PaymentIntent");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to create PaymentIntent. Check console/logs.");
    }
  };

  const handlePaymentSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }
    if (!clientSecret) {
      alert("No PaymentIntent clientSecret found. Please go back and try again.");
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      alert("No CardElement found!");
      return;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (error) {
      alert("Payment failed: " + error.message);
      console.error(error);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        const userID = currentUser ? currentUser.uid : null;

        // Save the investment to Firestore with fallback for carID
        const investmentData = {
          amount: investmentAmount,
          carID: carID || null,  // Ensure carID is not undefined
          date: Timestamp.now(),
          userID,
        };
        const investmentRef = await addDoc(collection(db, "investments"), investmentData);
        await updateDoc(doc(db, "investments", investmentRef.id), {
          investmentID: investmentRef.id,
        });

        // Calculate freebies and update Firestore
        const freeDaysEarned = calculateFreeDays(ownershipPercentage);
        const freebies = Array(freeDaysEarned).fill(userID);

        await updateDoc(doc(db, "cars", carID), {
          moneyRaised: moneyRaised + investmentAmount,
          freebies: arrayUnion(...freebies),
        });

        alert("Payment successful! Confirmation Email Sent");
        onClose();
      } catch (error) {
        console.error("Error creating investment document or updating car data:", error);
      }
    } else {
      alert("Payment not successful. Status: " + (paymentIntent?.status || "N/A"));
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      await createPaymentIntent();
      setCurrentStep(2);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePreviousStep = () => setCurrentStep((prev) => prev - 1);

  const formattedAmount = investmentAmount
    ? `$${investmentAmount.toLocaleString()}`
    : "";
  const taxRate = 0.035;
  const taxAmount = investmentAmount ? investmentAmount * taxRate : 0;
  const totalAmount = investmentAmount ? investmentAmount + taxAmount : 0;

  const rentalDiscount = calculateRentalDiscount(ownershipPercentage);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          width: "600px",
          maxWidth: "90%",
          textAlign: "center",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "none",
            border: "none",
            fontSize: "1.2em",
            cursor: "pointer",
          }}
        >
          &times;
        </button>

        {currentStep === 1 && (
          <>
            <h2 style={{ color: "#101213", marginBottom: "20px" }}>Enter Amount to Invest</h2>
            <h3
              style={{
                marginBottom: "30px",
                fontWeight: "bold",
                fontSize: "1.5em",
                color: "#101213",
              }}
            >
              {carName}
            </h3>
            <input
              type="text"
              value={formattedAmount}
              onChange={handleInputChange}
              style={{
                fontSize: "1.8em",
                textAlign: "center",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
                width: "80%",
                marginBottom: "20px",
                color: "black",
              }}
              placeholder="$"
            />
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "bold",
                  color: "black",
                }}
              >
                Adjust your percentage ownership:
              </label>
              <input
                type="range"
                min="0"
                max={maxOwnershipPercentage}
                step="1"
                value={ownershipPercentage}
                onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                style={{ width: "60%" }}
              />
            </div>
            <div
              style={{
                marginBottom: "15px",
                padding: "15px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                border: "1px solid #e0e0e0",
              }}
            >
              <div style={{ textAlign: "center", paddingLeft: "20px" }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.1em",
                    fontWeight: "bold",
                    color: "#007bff",
                  }}
                >
                  {ownershipPercentage}%
                </p>
                <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>
                  Ownership
                </p>
              </div>
              <div style={{ textAlign: "center", paddingLeft: "15px" }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.1em",
                    fontWeight: "bold",
                    color: "#28a745",
                  }}
                >
                  {calculateFreeDays(ownershipPercentage)} days
                </p>
                <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>
                  Free Days
                </p>
              </div>
              <div style={{ textAlign: "center", paddingRight: "10px" }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.1em",
                    fontWeight: "bold",
                    color: "#17a2b8",
                  }}
                >
                  {rentalDiscount}%
                </p>
                <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>
                  Rental Discount
                </p>
              </div>
            </div>
            <button
              onClick={handleNextStep}
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "10px 22px",
                fontSize: "1.5em",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Next
            </button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <h2 style={{ color: "#101213", marginBottom: "20px" }}>Checkout</h2>
            <h3 style={{ color: "#101213", marginBottom: "20px" }}>{carName}</h3>
            <div style={{ margin: "20px 0" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  marginBottom: "20px",
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "12px",
                        borderBottom: "2px solid #e0e0e0",
                        fontWeight: "bold",
                        color: "#555",
                      }}
                    >
                      Review
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "12px",
                        borderBottom: "2px solid #e0e0e0",
                        fontWeight: "bold",
                        color: "#555",
                      }}
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{
                        textAlign: "left",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <strong style={{ color: "#101213", marginBottom: "20px" }}>
                        Investment Amount
                      </strong>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                        color: "black",
                      }}
                    >
                      {formattedAmount}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        textAlign: "left",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                        color: "black",
                      }}
                    >
                      <strong>Processing Fee</strong>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                        color: "black",
                      }}
                    >
                      ${taxAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        textAlign: "left",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                        color: "black",
                      }}
                    >
                      <strong>Total</strong>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "10px",
                        borderBottom: "1px solid #e0e0e0",
                        fontSize: "1.3em",
                        fontWeight: "bold",
                        color: "#28a745",
                      }}
                    >
                      ${totalAmount.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "18px",
                      color: "#424770",
                      "::placeholder": { color: "#aab7c4" },
                    },
                    invalid: { color: "#9e2146" },
                  },
                }}
              />
            </div>

            <div>
              <button
                onClick={handlePreviousStep}
                style={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "10px 20px",
                  fontSize: "1.2em",
                  fontWeight: "bold",
                  cursor: "pointer",
                  marginRight: "10px",
                }}
              >
                Back
              </button>
              <button
                onClick={handlePaymentSubmit}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "10px 20px",
                  fontSize: "1.2em",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Complete Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvestmentModal;
